import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { User, Mail, Phone, MapPin, Package, Clock, CreditCard, LogOut, Edit2, Save, X, Loader2, QrCode, ShoppingBag, Heart, Smartphone, Wifi, Download, Copy, Check, ExternalLink, ChevronDown, Gift, TrendingDown, FileText, Navigation, Activity, BookOpen, Languages, Bell } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useWishlist } from '../context/WishlistContext';
import { useCurrency } from '../context/CurrencyContext';
import MyEsimsTab from './MyEsimsTab';
import ReferralTab from './ReferralTab';
import TripShare from './TripShare';
import DataMonitor from './DataMonitor';
import TravelDiary from './TravelDiary';
import Translator from './Translator';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AccountDashboard({ onNavigate, onProductClick }) {
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const { user, logout, updateProfile } = useAuth();
  const { wishlist, removeFromWishlist } = useWishlist();
  const [activeTab, setActiveTab] = useState('profile');
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [flashOffers, setFlashOffers] = useState(false);
  const [togglingFlash, setTogglingFlash] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('session_token') : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        country: user.country || '',
        postal_code: user.postal_code || ''
      });
      setFlashOffers(!!user.flash_offers_enabled);
    }
  }, [user]);

  const toggleFlashOffers = async () => {
    setTogglingFlash(true);
    try {
      const res = await axios.post(`${API}/auth/flash-offers/toggle`, {}, { headers });
      setFlashOffers(res.data.flash_offers_enabled);
    } catch { /* ignore */ }
    setTogglingFlash(false);
  };

  const fetchOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/auth/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(res.data.orders || []);
    } catch { /* ignore */ }
    finally { setLoadingOrders(false); }
  }, []);

  const fetchEsims = useCallback(() => {}, []);

  const fetchInvoices = useCallback(async () => {
    setLoadingInvoices(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/invoices/my-invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(res.data.invoices || []);
    } catch { /* ignore */ }
    finally { setLoadingInvoices(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'orders') fetchOrders();
    if (activeTab === 'invoices') fetchInvoices();
  }, [activeTab, fetchOrders, fetchInvoices]);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile(profileForm);
      setEditing(false);
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const handleLogout = async () => {
    await logout();
    onNavigate('home');
  };

  const tabs = [
    { key: 'profile', label: t('account.profile'), icon: User },
    { key: 'wishlist', label: t('account.wishlist'), icon: Heart, count: wishlist.length },
    { key: 'orders', label: t('account.orders'), icon: ShoppingBag },
    { key: 'invoices', label: t('account.invoices', 'Invoices'), icon: FileText },
    { key: 'esims', label: t('account.esims'), icon: QrCode },
    { key: 'monitor', label: 'Data Monitor', icon: Activity },
    { key: 'diary', label: 'Diario', icon: BookOpen },
    { key: 'translator', label: 'Traduttore', icon: Languages },
    { key: 'referral', label: t('account.referral') || 'Referral', icon: Gift },
    { key: 'trips', label: 'Condividi Viaggio', icon: Navigation }
  ];

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0f1419] pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-14 h-14 rounded-full border-2 border-teal-500/30" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-teal-500/20 flex items-center justify-center border-2 border-teal-500/30">
                  <User className="w-7 h-7 text-teal-400" />
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-white font-outfit" data-testid="account-name">{user.name}</h1>
                <p className="text-slate-400 text-sm" data-testid="account-email">{user.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 text-slate-400 hover:text-rose-400 transition-colors px-4 py-2 rounded-lg hover:bg-rose-500/10"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span>{t('account.logout')}</span>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 bg-white/5 rounded-xl p-1 mb-8 overflow-x-auto scrollbar-hide" data-testid="account-tabs">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center space-x-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.key
                    ? 'bg-teal-500 text-black'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`tab-${tab.key}`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
                {tab.count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    activeTab === tab.key ? 'bg-black/20' : 'bg-white/10'
                  }`}>{tab.count}</span>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'profile' && (
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6" data-testid="profile-section">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-white">{t('account.personalInfo')}</h2>
                {!editing ? (
                  <button
                    onClick={() => setEditing(true)}
                    className="flex items-center space-x-1 text-teal-400 hover:text-teal-300 text-sm"
                    data-testid="edit-profile-button"
                  >
                    <Edit2 className="w-4 h-4" />
                    <span>{t('account.edit')}</span>
                  </button>
                ) : (
                  <div className="flex space-x-2">
                    <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-white px-3 py-1 rounded-lg text-sm">
                      <X className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleSaveProfile}
                      disabled={saving}
                      className="flex items-center space-x-1 bg-teal-500 text-black px-4 py-1 rounded-lg text-sm font-medium"
                      data-testid="save-profile-button"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      <span>{t('account.save')}</span>
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { key: 'name', label: t('account.fullName'), icon: User },
                  { key: 'phone', label: t('account.phone'), icon: Phone },
                  { key: 'address', label: t('account.address'), icon: MapPin },
                  { key: 'city', label: t('account.city'), icon: MapPin },
                  { key: 'country', label: t('account.country'), icon: MapPin },
                  { key: 'postal_code', label: t('account.postalCode'), icon: Package }
                ].map(field => (
                  <div key={field.key}>
                    <label className="text-slate-400 text-xs mb-1 block">{field.label}</label>
                    {editing ? (
                      <input
                        type="text"
                        value={profileForm[field.key] || ''}
                        onChange={(e) => setProfileForm(p => ({ ...p, [field.key]: e.target.value }))}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                        data-testid={`profile-input-${field.key}`}
                      />
                    ) : (
                      <div className="flex items-center space-x-2 bg-white/5 rounded-xl px-4 py-2.5">
                        <field.icon className="w-4 h-4 text-slate-500" />
                        <span className="text-white text-sm" data-testid={`profile-value-${field.key}`}>
                          {user[field.key] || '-'}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-4 bg-white/5 rounded-xl px-4 py-2.5">
                <label className="text-slate-400 text-xs mb-1 block">Email</label>
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-slate-500" />
                  <span className="text-white text-sm">{user.email}</span>
                  {user.auth_provider === 'google' && (
                    <span className="text-xs bg-teal-500/20 text-teal-400 px-2 py-0.5 rounded-full">Google</span>
                  )}
                </div>
              </div>

              {/* Flash Offers Toggle */}
              <div className="mt-4 bg-gradient-to-r from-amber-500/5 to-orange-500/5 border border-amber-500/10 rounded-xl px-5 py-4" data-testid="flash-offers-section">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-amber-500/10 rounded-lg flex items-center justify-center">
                      <Bell className="w-4 h-4 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">{t('account.flashOffers', 'Offerte Flash')}</p>
                      <p className="text-slate-400 text-xs mt-0.5">{t('account.flashOffersDesc', 'Ricevi email quando i prezzi scendono per le tue destinazioni')}</p>
                    </div>
                  </div>
                  <button
                    onClick={toggleFlashOffers}
                    disabled={togglingFlash}
                    className={`relative w-12 h-6 rounded-full transition-colors ${flashOffers ? 'bg-amber-500' : 'bg-slate-700'}`}
                    data-testid="flash-offers-toggle"
                  >
                    {togglingFlash ? (
                      <Loader2 className="w-3 h-3 text-white animate-spin absolute top-1.5 left-1/2 -translate-x-1/2" />
                    ) : (
                      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${flashOffers ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'orders' && (
            <div data-testid="orders-section">
              {loadingOrders ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
                </div>
              ) : orders.length === 0 ? (
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-12 text-center">
                  <ShoppingBag className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-white font-semibold mb-2">{t('account.noOrders')}</h3>
                  <p className="text-slate-400 text-sm mb-6">{t('account.noOrdersDesc')}</p>
                  <button
                    onClick={() => onNavigate('shop')}
                    className="bg-teal-500 hover:bg-teal-400 text-black font-semibold px-6 py-2.5 rounded-full transition-all"
                    data-testid="browse-plans-button"
                  >
                    Browse Plans
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order, idx) => {
                    const isExpanded = expandedOrder === idx;
                    const statusMap = {
                      paid: { label: t('account.completed'), color: 'bg-emerald-400', textColor: 'text-emerald-400' },
                      initiated: { label: t('account.processing'), color: 'bg-amber-400', textColor: 'text-amber-400' },
                      pending: { label: t('account.pending'), color: 'bg-amber-400', textColor: 'text-amber-400' },
                      failed: { label: t('account.failed'), color: 'bg-rose-400', textColor: 'text-rose-400' },
                    };
                    const status = statusMap[order.payment_status] || { label: order.payment_status, color: 'bg-slate-500', textColor: 'text-slate-400' };
                    
                    return (
                      <div key={order.session_id || idx} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden" data-testid={`order-${idx}`}>
                        <button
                          onClick={() => setExpandedOrder(isExpanded ? null : idx)}
                          className="w-full p-5 flex items-center justify-between hover:bg-white/5 transition-colors"
                        >
                          <div className="flex items-center space-x-4">
                            <div className={`w-2.5 h-2.5 rounded-full ${status.color}`} />
                            <div className="text-left">
                              <span className={`font-medium text-sm ${status.textColor}`}>{status.label}</span>
                              <p className="text-slate-500 text-xs mt-0.5">
                                {new Date(order.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <span className="text-teal-400 font-bold font-mono">{formatPrice(order.amount)}</span>
                            <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </div>
                        </button>
                        
                        {isExpanded && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="border-t border-white/5 p-5">
                            {/* Order Timeline */}
                            <div className="mb-5">
                              <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-3">{t('account.orderStatus')}</h4>
                              <div className="flex items-center gap-0">
                                {[t('account.orderPlaced'), t('account.payment'), t('account.esimReady'), t('account.active')].map((step, i) => {
                                  const completed = order.payment_status === 'paid' ? i <= 2 : i <= 0;
                                  return (
                                    <div key={step} className="flex-1 flex items-center">
                                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                                        completed ? 'bg-teal-500 text-black' : 'bg-white/10 text-slate-500'
                                      }`}>
                                        {completed ? <Check className="w-3.5 h-3.5" /> : i + 1}
                                      </div>
                                      {i < 3 && <div className={`flex-1 h-0.5 mx-1 ${completed && i < 2 ? 'bg-teal-500' : 'bg-white/10'}`} />}
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="flex justify-between mt-1">
                                {[t('account.orderPlaced'), t('account.payment'), t('account.esimReady'), t('account.active')].map(step => (
                                  <span key={step} className="text-[10px] text-slate-500 flex-1 text-center">{step}</span>
                                ))}
                              </div>
                            </div>

                            {/* Order Items */}
                            {order.items && (
                              <div>
                                <h4 className="text-xs text-slate-500 uppercase tracking-wide mb-2">{t('account.items')}</h4>
                                <div className="space-y-2">
                                  {order.items.map((item, i) => (
                                    <div key={i} className="flex justify-between items-center bg-white/5 rounded-lg px-3 py-2">
                                      <span className="text-slate-300 text-sm">{item.name}</span>
                                      <div className="flex items-center space-x-3">
                                        <span className="text-slate-500 text-xs">x{item.quantity}</span>
                                        <span className="text-teal-400 font-mono text-sm">{formatPrice(item.price * item.quantity)}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
                              <div className="flex items-center space-x-2 text-xs text-slate-500">
                                <CreditCard className="w-3 h-3" />
                                <span>{order.payment_method === 'paypal' ? 'PayPal' : 'Stripe'}</span>
                                {order.order_number && (
                                  <>
                                    <span className="text-slate-600">|</span>
                                    <span>#{order.order_number}</span>
                                  </>
                                )}
                              </div>
                              {order.payment_status === 'paid' && (
                                <span className="text-xs text-emerald-400 flex items-center gap-1">
                                  <Check className="w-3 h-3" /> {t('account.emailSent')}
                                </span>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'wishlist' && (
            <div data-testid="wishlist-section">
              {wishlist.length === 0 ? (
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-12 text-center">
                  <Heart className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-white font-semibold mb-2">{t('account.emptyWishlist')}</h3>
                  <p className="text-slate-400 text-sm mb-6">{t('account.emptyWishlistDesc')}</p>
                  <button
                    onClick={() => onNavigate('shop')}
                    className="bg-teal-500 hover:bg-teal-400 text-black font-semibold px-6 py-2.5 rounded-full transition-all"
                    data-testid="browse-destinations-button"
                  >
                    {t('account.browseDestinations')}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {wishlist.map(item => (
                    <div
                      key={item.product_id}
                      className={`bg-white/5 backdrop-blur-sm border rounded-2xl overflow-hidden hover:border-teal-500/30 transition-all cursor-pointer relative ${item.price_dropped ? 'border-teal-500/40 ring-1 ring-teal-500/20' : 'border-white/10'}`}
                      onClick={() => onProductClick?.({ id: item.product_id, name: item.product_name, price: item.current_price || item.product_price, type: 'variable' })}
                      data-testid={`wishlist-item-${item.product_id}`}
                    >
                      {item.price_dropped && (
                        <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 bg-teal-500 text-black text-xs font-bold px-2.5 py-1 rounded-full shadow-lg shadow-teal-500/30" data-testid={`price-drop-badge-${item.product_id}`}>
                          <TrendingDown className="w-3 h-3" />
                          <span>-{item.drop_pct}%</span>
                        </div>
                      )}
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <h3 className="text-white font-semibold font-outfit line-clamp-1 flex-1">{item.product_name}</h3>
                          <button
                            onClick={(e) => { e.stopPropagation(); removeFromWishlist(item.product_id); }}
                            className="ml-2 p-1.5 rounded-full bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 transition-colors"
                            data-testid={`wishlist-remove-${item.product_id}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="mb-3">
                          {item.price_dropped && item.old_price ? (
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500 line-through text-sm font-mono">{formatPrice(item.old_price)}</span>
                              <span className="text-teal-400 font-bold font-mono">{t('products.from')} {formatPrice(item.current_price || item.product_price || 0)}</span>
                            </div>
                          ) : (
                            <p className="text-teal-400 font-bold font-mono">
                              {t('products.from')} {formatPrice(item.current_price || item.product_price || 0)}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-slate-500 text-xs">
                            {t('account.added')} {new Date(item.added_at).toLocaleDateString()}
                          </p>
                          {item.price_dropped && (
                            <span className="text-teal-400 text-xs font-medium animate-pulse">
                              {t('products.from').includes('da') ? 'Prezzo sceso!' : 'Price dropped!'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'invoices' && (
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6" data-testid="invoices-section">
              <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <FileText className="w-5 h-5 text-teal-400" />
                {t('account.invoices', 'Invoices')}
              </h2>
              {loadingInvoices ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 text-teal-400 animate-spin" />
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">{t('account.noInvoices', 'No invoices yet')}</p>
                  <p className="text-slate-500 text-sm mt-1">{t('account.noInvoicesDesc', 'Request an invoice during checkout and it will appear here.')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {invoices.map((inv) => (
                    <div key={inv.id} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between" data-testid={`invoice-${inv.invoice_number}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-teal-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium font-mono text-sm">{inv.invoice_number}</p>
                          <p className="text-slate-400 text-xs">
                            {t('account.order', 'Order')} #{inv.order_number} &middot; {new Date(inv.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-white font-bold font-mono">{formatPrice(inv.amount)}</p>
                          <p className="text-xs text-slate-500">{inv.entity === 'eu' ? 'EU (Poland)' : 'USA'}</p>
                        </div>
                        <a
                          href={`${API}/invoices/${inv.id}/pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 bg-teal-500/20 hover:bg-teal-500/30 text-teal-400 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                          data-testid={`download-invoice-${inv.invoice_number}`}
                        >
                          <Download className="w-4 h-4" />
                          PDF
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'esims' && (
            <MyEsimsTab onNavigate={onNavigate} />
          )}

          {activeTab === 'monitor' && (
            <DataMonitor onNavigate={onNavigate} />
          )}

          {activeTab === 'diary' && (
            <TravelDiary />
          )}

          {activeTab === 'translator' && (
            <Translator embedded={true} />
          )}

          {activeTab === 'referral' && (
            <ReferralTab />
          )}

          {activeTab === 'trips' && (
            <TripShare token={token} />
          )}
        </motion.div>
      </div>
    </div>
  );
}
